import React, { useState, useEffect, useCallback } from 'react';
import ConfirmModal from './ConfirmModal';
import PromptModal from './PromptModal';
import './TemplateManager.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Categories editable in a template. FIT_SPECIALIST is intentionally excluded —
// fits are managed per-dealership and never templated.
const TEMPLATE_CATEGORIES = [
  { key: 'LENDER', label: 'Lenders' },
  { key: 'FI_PROVIDER', label: 'F&I Providers' },
  { key: 'MOTORCYCLE_MAKE', label: 'Motorcycle Makes' },
  { key: 'MOTORCYCLE_MODEL', label: 'Motorcycle Models' },
  { key: 'SOURCE', label: 'Sources' },
  { key: 'DEAL_CLASS', label: 'Deal Classes' },
  { key: 'STATUS', label: 'Statuses' },
];

// Value and label are always identical for these dropdowns, so the editor
// exposes a single field and stores it as both.
const emptyAdd = { value: '', groupBy: '', familyGroup: '' };

export default function TemplateManager() {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [detail, setDetail] = useState(null);
  const [activeCategory, setActiveCategory] = useState('LENDER');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text }
  const [addForm, setAddForm] = useState(emptyAdd);
  const [editing, setEditing] = useState(null); // { id, value, label, groupBy, familyGroup }
  const [confirm, setConfirm] = useState({ isOpen: false });
  const [prompt, setPrompt] = useState({ isOpen: false });

  const token = () => localStorage.getItem('token');

  const api = useCallback(async (path, options = {}) => {
    const response = await fetch(`${API_URL}/api/templates${path}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token()}`,
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `Request failed (${response.status})`);
    return data;
  }, []);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('');
      const list = Array.isArray(data) ? data : [];
      setTemplates(list);
      setSelectedId((prev) => prev || list[0]?.id || '');
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    try {
      const data = await api(`/${id}`);
      setDetail(data);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  }, [api]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);
  useEffect(() => { fetchDetail(selectedId); setEditing(null); setAddForm(emptyAdd); }, [selectedId, fetchDetail]);

  const reload = async () => { await fetchDetail(selectedId); await fetchTemplates(); };

  const currentOptions = () => {
    const cat = detail?.categories?.find((c) => c.category === activeCategory);
    return (cat?.options || []).slice().sort((a, b) => a.displayOrder - b.displayOrder);
  };
  const isModel = activeCategory === 'MOTORCYCLE_MODEL';

  // ---- template-level actions ----
  const createTemplate = () => setPrompt({
    isOpen: true,
    title: 'New Template',
    message: 'Name for the new template',
    placeholder: 'e.g., Indian Motorcycle',
    onSubmit: async (name) => {
      if (!name?.trim()) return;
      const created = await api('', { method: 'POST', body: JSON.stringify({ name: name.trim() }) });
      await fetchTemplates();
      setSelectedId(created.id);
      setMessage({ type: 'success', text: `Template "${created.name}" created.` });
    },
  });

  const renameTemplate = () => setPrompt({
    isOpen: true,
    title: 'Rename Template',
    message: 'New name',
    defaultValue: detail?.name || '',
    onSubmit: async (name) => {
      if (!name?.trim()) return;
      await api(`/${selectedId}`, { method: 'PUT', body: JSON.stringify({ name: name.trim(), description: detail?.description }) });
      await reload();
      setMessage({ type: 'success', text: 'Template renamed.' });
    },
  });

  const deleteTemplate = () => setConfirm({
    isOpen: true,
    title: 'Delete Template',
    message: `Delete "${detail?.name}"? This removes the template only—dealerships already created keep their dropdowns.`,
    confirmText: 'Delete',
    onConfirm: async () => {
      await api(`/${selectedId}`, { method: 'DELETE' });
      setSelectedId('');
      await fetchTemplates();
      setMessage({ type: 'success', text: 'Template deleted.' });
    },
  });

  const pushToDealerships = () => setConfirm({
    isOpen: true,
    title: 'Push to Linked Dealerships',
    message: `Add this template's options to every dealership using "${detail?.name}"? New options are added; nothing existing is removed or overwritten, and fits are never touched.`,
    confirmText: 'Push',
    onConfirm: async () => {
      const res = await api(`/${selectedId}/push`, { method: 'POST' });
      setMessage({
        type: 'success',
        text: `Pushed: ${res.optionsAdded} option(s) added across ${res.dealershipsAffected} of ${res.dealershipsLinked} linked dealership(s).`,
      });
    },
  });

  // ---- option-level actions ----
  const addOption = async (e) => {
    e.preventDefault();
    if (!addForm.value.trim()) return;
    setBusy(true);
    try {
      await api(`/${selectedId}/categories/${activeCategory}/options`, {
        method: 'POST',
        body: JSON.stringify({
          value: addForm.value.trim(),
          label: addForm.value.trim(),
          groupBy: isModel ? (addForm.groupBy.trim() || null) : null,
          familyGroup: isModel ? (addForm.familyGroup.trim() || null) : null,
        }),
      });
      setAddForm(emptyAdd);
      await fetchDetail(selectedId);
      setMessage({ type: 'success', text: 'Option added.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api(`/${selectedId}/options/${editing.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          value: editing.value.trim(),
          label: editing.value.trim(),
          groupBy: isModel ? (editing.groupBy?.trim() || null) : undefined,
          familyGroup: isModel ? (editing.familyGroup?.trim() || null) : undefined,
        }),
      });
      setEditing(null);
      await fetchDetail(selectedId);
      setMessage({ type: 'success', text: 'Option updated.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const deleteOption = (opt) => setConfirm({
    isOpen: true,
    title: 'Delete Option',
    message: `Remove "${opt.label || opt.value}" from this template? (Dealerships already created are unaffected.)`,
    confirmText: 'Delete',
    onConfirm: async () => {
      await api(`/${selectedId}/options/${opt.id}`, { method: 'DELETE' });
      await fetchDetail(selectedId);
      setMessage({ type: 'success', text: 'Option deleted.' });
    },
  });

  const moveOption = async (index, dir) => {
    const opts = currentOptions();
    const target = index + dir;
    if (target < 0 || target >= opts.length) return;
    const reordered = opts.slice();
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setBusy(true);
    try {
      await api(`/${selectedId}/categories/${activeCategory}/reorder`, {
        method: 'PUT',
        body: JSON.stringify({ optionIds: reordered.map((o) => o.id) }),
      });
      await fetchDetail(selectedId);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const opts = currentOptions();

  return (
    <div className="tm">
      <div className="tm-toolbar">
        <div className="tm-toolbar-left">
          <label>Template:</label>
          <select className="tm-select" value={selectedId} onChange={(e) => setSelectedId(e.target.value)} disabled={loading}>
            {templates.length === 0 && <option value="">No templates</option>}
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button className="tm-btn" onClick={createTemplate} disabled={busy}>+ New</button>
          {detail && <button className="tm-btn" onClick={renameTemplate} disabled={busy}>Rename</button>}
          {detail && <button className="tm-btn tm-btn-danger" onClick={deleteTemplate} disabled={busy}>Delete</button>}
        </div>
        {detail && (
          <button className="tm-btn tm-btn-primary" onClick={pushToDealerships} disabled={busy}>
            Push to linked dealerships
          </button>
        )}
      </div>

      {message && (
        <div className={`tm-msg ${message.type === 'error' ? 'tm-msg-error' : 'tm-msg-success'}`}>
          {message.text}
        </div>
      )}

      {!detail ? (
        <div className="tm-empty">{loading ? 'Loading…' : 'Select or create a template to edit its dropdowns.'}</div>
      ) : (
        <>
          <div className="tm-cat-tabs">
            {TEMPLATE_CATEGORIES.map((c) => {
              const count = detail.categories?.find((x) => x.category === c.key)?.options?.length || 0;
              return (
                <button
                  key={c.key}
                  className={`tm-cat ${activeCategory === c.key ? 'active' : ''}`}
                  onClick={() => { setActiveCategory(c.key); setEditing(null); setAddForm(emptyAdd); }}
                >
                  {c.label} <span className="tm-count">({count})</span>
                </button>
              );
            })}
          </div>

          {/* Add form */}
          <form className="tm-addform" onSubmit={addOption}>
            <div className="tm-field">
              <label>Value *</label>
              <input value={addForm.value} onChange={(e) => setAddForm({ ...addForm, value: e.target.value })} placeholder="e.g., FLHX" required />
            </div>
            {isModel && (
              <>
                <div className="tm-field">
                  <label>Make (groupBy)</label>
                  <input value={addForm.groupBy} onChange={(e) => setAddForm({ ...addForm, groupBy: e.target.value })} placeholder="e.g., Harley-Davidson" />
                </div>
                <div className="tm-field">
                  <label>Family</label>
                  <input value={addForm.familyGroup} onChange={(e) => setAddForm({ ...addForm, familyGroup: e.target.value })} placeholder="e.g., Touring" />
                </div>
              </>
            )}
            <button type="submit" className="tm-btn tm-btn-primary" disabled={busy || !addForm.value.trim()}>
              Add
            </button>
          </form>

          {/* Options list */}
          {opts.length === 0 ? (
            <div className="tm-empty">No options yet in this category.</div>
          ) : (
            <div className="tm-table-wrap">
            <table className="tm-table">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Order</th>
                  <th>Value</th>
                  {isModel && <th>Make</th>}
                  {isModel && <th>Family</th>}
                  <th style={{ width: 150 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {opts.map((opt, index) => {
                  const isEditing = editing?.id === opt.id;
                  return (
                    <tr key={opt.id}>
                      <td className="tm-nowrap">
                        <button className="tm-btn tm-icon-btn" onClick={() => moveOption(index, -1)} disabled={busy || index === 0} title="Move up">↑</button>
                        <button className="tm-btn tm-icon-btn" onClick={() => moveOption(index, 1)} disabled={busy || index === opts.length - 1} title="Move down">↓</button>
                      </td>
                      {isEditing ? (
                        <>
                          <td><input value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} /></td>
                          {isModel && <td><input value={editing.groupBy || ''} onChange={(e) => setEditing({ ...editing, groupBy: e.target.value })} /></td>}
                          {isModel && <td><input value={editing.familyGroup || ''} onChange={(e) => setEditing({ ...editing, familyGroup: e.target.value })} /></td>}
                          <td className="tm-nowrap">
                            <button className="tm-btn tm-btn-sm tm-btn-primary" onClick={saveEdit} disabled={busy || !editing.value.trim()}>Save</button>
                            <button className="tm-btn tm-btn-sm" onClick={() => setEditing(null)} disabled={busy}>Cancel</button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>{opt.value}</td>
                          {isModel && <td className="tm-muted">{opt.groupBy || '—'}</td>}
                          {isModel && <td className="tm-muted">{opt.familyGroup || '—'}</td>}
                          <td className="tm-nowrap">
                            <button className="tm-btn tm-btn-sm" onClick={() => setEditing({ id: opt.id, value: opt.value, groupBy: opt.groupBy, familyGroup: opt.familyGroup })} disabled={busy}>Edit</button>
                            <button className="tm-btn tm-btn-sm tm-btn-danger" onClick={() => deleteOption(opt)} disabled={busy}>Delete</button>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </>
      )}

      <ConfirmModal
        isOpen={confirm.isOpen}
        onClose={() => setConfirm({ isOpen: false })}
        onConfirm={async () => {
          try { await confirm.onConfirm?.(); }
          catch (err) { setMessage({ type: 'error', text: err.message }); }
        }}
        title={confirm.title}
        message={confirm.message}
        confirmText={confirm.confirmText}
      />
      <PromptModal
        isOpen={prompt.isOpen}
        onClose={() => setPrompt({ isOpen: false })}
        onSubmit={async (v) => {
          try { await prompt.onSubmit?.(v); }
          catch (err) { setMessage({ type: 'error', text: err.message }); }
        }}
        title={prompt.title}
        message={prompt.message}
        placeholder={prompt.placeholder}
        defaultValue={prompt.defaultValue}
      />
    </div>
  );
}
